import { CommonModule } from '@angular/common';
import { Component, OnInit , ChangeDetectionStrategy } from '@angular/core';
import { environment } from '../../environments/environment';
import { RouterModule } from '@angular/router';

interface NavigationLink {
  label: string;
  routerLink?: string;
  href?: string;
}

interface NavigationColumn {
  items: NavigationLink[];
}

interface SocialLink {
  label: string;
  href: string;
  icon: string;
  alt: string;
  external: boolean;
}

interface WhyScripaySection {
  title: string;
  description: string;
}

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterComponent implements OnInit {
  baseUrl: string = environment.apiUrl;
  currentYear: number = new Date().getFullYear();

  navigationLinks: NavigationColumn[] = [];
  whyScripaySections: WhyScripaySection[] = [];
  socialLinks: SocialLink[] = [];

  ngOnInit(): void {
    this.initializeNavigationLinks();
    this.initializeWhyScripaySections();
    this.initializeSocialLinks();
  }

  private initializeNavigationLinks(): void {
    this.navigationLinks = [
      {
        items: [
          { label: 'Pay in', routerLink: 'products' },
          { label: 'Pay out', routerLink: 'products' },
          { label: 'OTC Services', routerLink: 'products' },
          { label: 'Transaction Management', routerLink: 'products' }
        ]
      },
      {
        items: [
          { label: 'API Documentation', href: this.baseUrl + 'docs' },
          { label: 'SDKs & Libraries', href: this.baseUrl + 'docs' },
          { label: 'Webhooks', href: this.baseUrl + 'docs' },
          { label: 'Sandbox', href: this.baseUrl + 'docs' },
          { label: 'Wallet API', href: this.baseUrl + 'docs' }
        ]
      },
      {
        items: [
          { label: 'Privacy Policy', routerLink: 'privacy-policy' },
          { label: 'Cookie Policy', routerLink: 'cookie-policy' },
          { label: 'Terms & Conditions', routerLink: 'terms-and-conditions' },
          { label: 'KYC & Onboarding', href: this.baseUrl + 'register-form' }
        ]
      },
      {
        items: [
          { label: 'Help Center', routerLink: 'contact-us' },
          { label: 'Contact Support', routerLink: 'contact-us' },
          { label: 'About Us', routerLink: 'about-us' },
          { label: 'Careers', routerLink: 'careers' }
        ]
      }
    ];
  }

  private initializeWhyScripaySections(): void {
    this.whyScripaySections = [
      {
        title: 'Why is Scripay important to your business?',
        description: `Scripay streamlines your payment operations by bringing all transactions, from mobile money to bank transfers, under one secure platform. It helps your business automate collections, manage wallets, and track transactions in real time. With powerful analytics and flexible configurations, Scripay makes it easier to scale, improve cash flow visibility, and deliver a seamless payment experience to your customers.`
      },
      {
        title: 'Why is Scripay important to developers?',
        description: `Scripay empowers developers with a robust, well-documented API suite that simplifies payment integrations. It offers SDKs, webhooks, and sandbox testing environments for quick setup and reliable deployment. Designed with developers in mind, Scripay ensures secure authentication, clear endpoints, and real-time updates, allowing you to build and launch payment features efficiently with minimal friction.`
      }
    ];
  }

  private initializeSocialLinks(): void {
    this.socialLinks = [
      {
        label: 'LinkedIn',
        href: 'https://www.linkedin.com/company/scripay',
        icon: 'assets/icons/footer/LinkedIn.svg',
        alt: 'ScriPay LinkedIn',
        external: true
      },
      {
        label: 'Facebook',
        href: '#',
        icon: 'assets/icons/footer/Facebook.svg',
        alt: 'ScriPay Facebook',
        external: false
      },
      {
        label: 'X (Twitter)',
        href: '#',
        icon: 'assets/icons/footer/Twitter.svg',
        alt: 'ScriPay Twitter',
        external: false
      },
      {
        label: 'YouTube',
        href: '#',
        icon: 'assets/icons/footer/YouTube.svg',
        alt: 'ScriPay YouTube',
        external: false
      },
      {
        label: 'Instagram',
        href: '#',
        icon: 'assets/icons/footer/Instagram.svg',
        alt: 'ScriPay Instagram',
        external: false
      },
      {
        label: 'TikTok',
        href: '#',
        icon: 'assets/icons/footer/TikTok.svg',
        alt: 'ScriPay TikTok',
        external: false
      }
    ];
  }
}
